/**
 * Equipment hook that reads from DataHub AppSheet Generators collection
 *
 * This hook reads equipment data from the appsheet_generators collection
 * which is synced via the DataHub AppSheet Service App connector.
 *
 * The data is read-only from the sync perspective - local edits can be made
 * but won't sync back to AppSheet.
 */
import { parseAppSheetBoolean } from '../lib/appsheet/yesNo';
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import {
  collection,
  query,
  getDocs,
  doc,
  getDoc,
  orderBy,
  where,
  limit,
  startAfter,
  Timestamp,
  DocumentSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from '../lib/firebase/authReadyCallable';
import { db, functions } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { isSoftDeleted } from '../lib/appsheet/softDelete';
import type { Equipment, AppSheetSyncResult } from '../types/equipment';
import { DataHubGeneratorSchema, validateDataHubData } from '../types/datahub-schemas';
import { localDateFromParts } from '@officehub/shared';

// DataHub AppSheet Generator type (from appsheet_generators collection)
// Uses AppSheet original field names with spaces
export interface DataHubGenerator {
  hubDocId: string;
  appId: string;
  syncedAt: Timestamp;

  // AppSheet identifiers
  'Row ID'?: string;
  'Generator ID'?: string;

  // Customer info (AppSheet field names)
  'Client Name': string;
  'Client Name Sub-Account'?: string;
  'Management Company'?: string;
  'Site Name'?: string;
  'Client Type'?: string;
  'Client Notes'?: string;
  'Primary Contact'?: string;
  'Contacts'?: string;

  // Location
  /**
   * READONLY AppSheet formula — the concatenated full address
   * ("123 Main St, Tracy, CA 95376"). Never use this to seed an editable
   * street input; see `Generator Address Street` for the raw street part.
   */
  'Generator Address'?: string;
  /** Raw, writable street line (SHARED owner) — the real street-only field. */
  'Generator Address Street'?: string;
  'Generator Address City'?: string;
  'Generator Address State'?: string;
  'Equipment Address Zip'?: string;
  'GPS'?: string;
  'Access Instructions'?: string;

  // Equipment type
  'Generator Type'?: string;

  // Generator specs
  'Generator Make'?: string;
  'Generator Model'?: string;
  'Generator Serial Number'?: string;
  'Generator Spec Number'?: string;
  'Generator KW'?: number | string;
  'Voltage'?: string;
  'Amps'?: string;
  'Fuel'?: string;
  'Generator Notes'?: string;

  // Engine specs
  'Engine Make'?: string;
  'Engine Model'?: string;
  'Engine Serial Number'?: string;

  // Transfer switch specs
  'ATS Make'?: string;
  'ATS Model'?: string;
  'ATS Serial Number'?: string;
  'ATS2'?: string | boolean;
  'ATS 2 Make'?: string;
  'ATS 2 Model'?: string;
  'ATS 2 Serial Number'?: string;

  // Alternator specs
  'Alternator Make'?: string;
  'Alternator Model'?: string;
  'Alternator Serial Number'?: string;

  // Status and administrative
  'Status'?: string;
  'Branch'?: string;
  'Inactive Notes'?: string;
  'Prevailing Wage'?: string | boolean;
  'Verified'?: string | boolean;
  'Drive URL'?: string;

  // Service info
  'Install Date'?: string;
  'OPG Installed'?: string | boolean;
  'Service Contract'?: string | boolean;
  'Service Contract File'?: string;
  'Last Completed Service Date'?: string;
  'Next Service To Schedule'?: string;
  'Next Scheduled Visit Reminder'?: string;
  'Warranty Expires'?: string;
}

/**
 * Parse a date string from AppSheet into a Date object
 * AppSheet dates can be in various formats: MM/DD/YYYY, YYYY-MM-DD, etc.
 * Also handles Firestore Timestamp objects that may come from synced data.
 */
function parseAppSheetDate(dateStr?: string | Timestamp | unknown): Date | undefined {
  if (!dateStr) return undefined;

  // Handle Firestore Timestamp objects
  if (dateStr instanceof Timestamp) {
    return dateStr.toDate();
  }
  if (typeof dateStr === 'object' && dateStr !== null && 'toDate' in dateStr && typeof (dateStr as Timestamp).toDate === 'function') {
    return (dateStr as Timestamp).toDate();
  }

  // Must be a string from here on
  if (typeof dateStr !== 'string') return undefined;

  // Try parsing as ISO date first
  const isoDate = new Date(dateStr);
  if (!isNaN(isoDate.getTime())) {
    return isoDate;
  }

  // Try parsing MM/DD/YYYY format
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const [month, day, year] = parts;
    const parsedDate = localDateFromParts(parseInt(year), parseInt(month), parseInt(day));
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate;
    }
  }

  return undefined;
}

/**
 * Map DataHub Generator to Equipment type for UI compatibility
 * Reads from AppSheet field names (with spaces) and maps to camelCase Equipment type
 */

export function mapGeneratorToEquipment(gen: DataHubGenerator): Equipment {
  // Parse kW as number (AppSheet may send as string)
  const kW = gen['Generator KW'];
  const parsedKW = typeof kW === 'string' ? parseFloat(kW) : kW;

  return {
    equipmentId: gen.hubDocId,
    organizationId: '', // DataHub data is org-agnostic
    appSheetGeneratorId: gen['Generator ID'] || gen['Row ID'] || gen.hubDocId,
    // Never the fallbacks above: a quote links the unit by this (#8450 code M3).
    generatorIdFromAppSheet: typeof gen['Generator ID'] === 'string' ? gen['Generator ID'].trim() || undefined : undefined,

    // Customer info
    customerName: gen['Client Name'] || '',
    clientNameSubAccount: gen['Client Name Sub-Account'],
    managementCompany: gen['Management Company'],
    siteName: gen['Site Name'],
    clientType: mapClientType(gen['Client Type']),
    clientNotes: gen['Client Notes'],
    primaryContactId: gen['Primary Contact'],
    contacts: gen['Contacts'] ? gen['Contacts'].split(',').map((c) => c.trim()) : undefined,

    // Location
    address: {
      // MUST be the raw street part, NOT 'Generator Address' — that field is a
      // READONLY AppSheet formula holding the concatenated full address
      // ("123 Main St, Tracy, CA 95376"). Seeding the Edit modal's Street input
      // from it round-tripped the whole address back into
      // 'Generator Address Street', degrading the field on every open+save.
      street: gen['Generator Address Street'] || '',
      city: gen['Generator Address City'] || '',
      state: gen['Generator Address State'] || '',
      zip: gen['Equipment Address Zip'] || '',
    },
    gps: gen['GPS'],
    accessInstructions: gen['Access Instructions'],

    // Equipment details
    equipmentType: mapEquipmentType(gen['Generator Type']),
    status: mapStatus(gen['Status']),
    rawStatus: gen['Status'],

    // Generator specs
    generatorKW: isNaN(parsedKW as number) ? undefined : parsedKW as number,
    voltage: gen['Voltage'],
    amps: gen['Amps'],
    fuel: gen['Fuel'],
    generatorMake: gen['Generator Make'],
    generatorModel: gen['Generator Model'],
    generatorSerialNumber: gen['Generator Serial Number'],
    generatorSpecNumber: gen['Generator Spec Number'],
    generatorNotes: gen['Generator Notes'],

    // Engine
    engineMake: gen['Engine Make'],
    engineModel: gen['Engine Model'],
    engineSerialNumber: gen['Engine Serial Number'],

    // ATS
    atsMake: gen['ATS Make'],
    atsModel: gen['ATS Model'],
    atsSerialNumber: gen['ATS Serial Number'],
    // Tri-state ON PURPOSE: a BLANK AppSheet cell stays undefined (66 live rows).
    // Collapsing blank -> false made the Edit modal transmit ats2:false on a
    // no-op save — phantom-stamping _ohEditedAt and writing "N" to AppSheet on
    // a row nobody edited (red-team ATTACK 1). Blank is a third state.
    ats2: gen['ATS2'] === undefined || gen['ATS2'] === null || gen['ATS2'] === ''
      ? undefined
      : parseAppSheetBoolean(gen['ATS2']),
    ats2Make: gen['ATS 2 Make'],
    ats2Model: gen['ATS 2 Model'],
    ats2SerialNumber: gen['ATS 2 Serial Number'],

    // Alternator
    alternatorMake: gen['Alternator Make'],
    alternatorModel: gen['Alternator Model'],
    alternatorSerialNumber: gen['Alternator Serial Number'],

    // Administrative
    branch: gen['Branch'],
    inactiveNotes: gen['Inactive Notes'],
    prevailingWage: parseAppSheetBoolean(gen['Prevailing Wage']),
    verified: parseAppSheetBoolean(gen['Verified']),
    driveURL: gen['Drive URL'],

    // Service
    installDate: parseAppSheetDate(gen['Install Date']),
    opgInstalled: parseAppSheetBoolean(gen['OPG Installed']),
    serviceContract: parseAppSheetBoolean(gen['Service Contract']),
    serviceContractFile: gen['Service Contract File'],
    lastCompletedServiceDate: parseAppSheetDate(gen['Last Completed Service Date']),
    nextServiceToSchedule: parseAppSheetDate(gen['Next Service To Schedule']),
    // FREE TEXT, not a date (live scan 2026-09-09: all 134 non-blank values are
    // notes/quote refs like "sq: S251113-C0H"). parseAppSheetDate destroyed the
    // value before the Edit modal saw it — undefined for real notes, a spurious
    // Date for the one "04/01/2026"-style value. Pass through verbatim.
    nextScheduledVisitReminder: gen['Next Scheduled Visit Reminder'],
    warrantyExpires: parseAppSheetDate(gen['Warranty Expires']),

    // Metadata
    createdAt: gen.syncedAt,
    updatedAt: gen.syncedAt,
    lastSyncedFromAppSheet: gen.syncedAt,
  };
}

const VALID_EQUIPMENT_TYPES: readonly string[] = [
  'Air-Cooled Standby Gen', 'Liquid-Cooled Standby Gen', 'Portable Customer Gen',
  'Rental Gen', 'Fire Pump', 'Pump', 'Battery Storage System', 'ATS Only', 'Other',
];

function mapEquipmentType(type?: string): Equipment['equipmentType'] {
  if (!type) return 'Liquid-Cooled Standby Gen';
  if (VALID_EQUIPMENT_TYPES.includes(type)) return type as Equipment['equipmentType'];
  return 'Other';
}

function mapClientType(type?: string): Equipment['clientType'] {
  if (!type) return undefined;
  const validTypes = ['Commercial', 'Residential', 'Government', 'Rental Company', 'Contractor', 'Property Manager'];
  if (validTypes.includes(type)) return type as Equipment['clientType'];
  return 'Commercial';
}

function mapStatus(status?: string): Equipment['status'] {
  if (!status) return 'Active';
  const statusLower = status.toLowerCase();
  if (statusLower.includes('inactive')) return 'Inactive';
  if (statusLower.includes('dormant')) return 'Dormant';
  if (statusLower.includes('credit')) return 'Credit Hold';
  if (statusLower.includes('removed')) return 'Removed';
  if (statusLower.includes('past rental')) return 'Past Rental';
  if (statusLower.includes('active rental')) return 'Active Rental';
  if (statusLower.includes('scheduled')) return 'Active Scheduled';
  return 'Active';
}

// Query keys factory for consistent cache management
export const dataHubEquipmentKeys = {
  all: ['datahub', 'equipment'] as const,
  list: () => [...dataHubEquipmentKeys.all, 'list'] as const,
  detail: (id: string) => [...dataHubEquipmentKeys.all, 'detail', id] as const,
  byCustomer: (customerName: string) => [...dataHubEquipmentKeys.all, 'customer', customerName] as const,
  byCustomerPrefix: (prefix: string) => [...dataHubEquipmentKeys.all, 'customerPrefix', prefix] as const,
  paginated: () => [...dataHubEquipmentKeys.all, 'paginated'] as const,
};

/**
 * Fetch equipment from DataHub appsheet_generators collection
 * Limited to 2500 records as a safety cap (~2000 expected).
 */
export function useDataHubEquipment() {
  return useQuery({
    queryKey: dataHubEquipmentKeys.list(),
    queryFn: async (): Promise<Equipment[]> => {
      // Note: AppSheet data uses original column names with spaces
      // Cap raised from 2500 (recordCount was already ~3085, i.e. already
      // truncating ~19% of equipment before this fix) to a value safely
      // above realistic fleet size, with a loud warning if it's ever hit —
      // this is master/fleet data (slow-growing), not an unbounded log, so a
      // generous cap + truncation warning (cap_warn) is appropriate rather
      // than full pagination; useDataHubEquipmentPaginated() already exists
      // as the fully-paginated alternative for callers that need guaranteed
      // completeness.
      const EQUIPMENT_FETCH_CAP = 10000;
      const q = query(
        collection(db, COLLECTIONS.APPSHEET_GENERATORS),
        orderBy('Client Name', 'asc'),
        limit(EQUIPMENT_FETCH_CAP)
      );

      const snapshot = await getDocs(q);
      if (snapshot.docs.length === EQUIPMENT_FETCH_CAP) {
        // eslint-disable-next-line no-console
        console.error(
          `useDataHubEquipment: fetch hit EQUIPMENT_FETCH_CAP (${EQUIPMENT_FETCH_CAP}); ` +
            'equipment list may be truncated, silently hiding some customers\' units.',
        );
      }
      return snapshot.docs.filter((d) => !isSoftDeleted(d.data())).map((docSnapshot) => {
        const rawData = docSnapshot.data();
        const { data } = validateDataHubData(
          DataHubGeneratorSchema,
          rawData,
          docSnapshot.id,
          'Generator'
        );

        // Cast to DataHubGenerator - validation has either confirmed structure or logged warning
        const generatorData = data as unknown as Omit<DataHubGenerator, 'hubDocId'>;
        return mapGeneratorToEquipment({ ...generatorData, hubDocId: docSnapshot.id });
      });
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch single equipment by ID from DataHub
 */
export function useDataHubEquipmentById(equipmentId?: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: dataHubEquipmentKeys.detail(equipmentId || ''),
    queryFn: async (): Promise<Equipment | null> => {
      if (!equipmentId) return null;

      const docRef = doc(db, COLLECTIONS.APPSHEET_GENERATORS, equipmentId);
      const docSnap = await getDoc(docRef);

      // Soft-deleted equipment is treated as not found (active-view read).
      if (!docSnap.exists() || isSoftDeleted(docSnap.data())) return null;

      const rawData = docSnap.data();
      const { data } = validateDataHubData(
        DataHubGeneratorSchema,
        rawData,
        docSnap.id,
        'Generator'
      );

      // Cast to DataHubGenerator - validation has either confirmed structure or logged warning
      const generatorData = data as unknown as Omit<DataHubGenerator, 'hubDocId'>;
      return mapGeneratorToEquipment({ ...generatorData, hubDocId: docSnap.id });
    },
    enabled: !!equipmentId,
    staleTime: 1000 * 60 * 5,
    initialData: () => {
      const lists = queryClient.getQueriesData<Equipment[]>({ queryKey: dataHubEquipmentKeys.list() });
      for (const [, items] of lists) {
        if (!Array.isArray(items)) continue;
        const found = items?.find((e) => e.equipmentId === equipmentId);
        if (found) return found;
      }
      return undefined;
    },
    initialDataUpdatedAt: 0,
  });
}

/**
 * Search equipment by customer name
 */
export function useDataHubEquipmentByCustomer(customerName?: string) {
  const { data: allEquipment, ...rest } = useDataHubEquipment();

  const filteredEquipment = customerName
    ? allEquipment?.filter(e =>
        e.customerName.toLowerCase().includes(customerName.toLowerCase())
      )
    : allEquipment;

  return { data: filteredEquipment, ...rest };
}

/**
 * Trigger sync of AppSheet Service App via DataHub
 * This calls the DataHub sync function for the AppSheet connection
 */
export function useSyncDataHubEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (connectionId: string | void): Promise<AppSheetSyncResult> => {
      // Call the DataHub AppSheet sync function
      const syncAppSheet = httpsCallable<
        { connectionId?: string; tableName?: string },
        { success: boolean; message: string; stats?: { added: number; updated: number; deleted: number } }
      >(functions, 'dataHub_syncAppSheetConnection');

      const result = await syncAppSheet({
        connectionId: connectionId || undefined,
        tableName: 'Generators',
      });

      return {
        success: result.data.success,
        message: result.data.message,
        synced: (result.data.stats?.added || 0) + (result.data.stats?.updated || 0),
        failed: 0,
      };
    },
    onSuccess: () => {
      // Invalidate equipment queries to refetch data
      queryClient.invalidateQueries({ queryKey: dataHubEquipmentKeys.all });
    },
  });
}

/**
 * Get equipment stats from DataHub
 */
export function useDataHubEquipmentStats() {
  const { data: equipment } = useDataHubEquipment();

  return useMemo(() => ({
    total: equipment?.length || 0,
    active: equipment?.filter(e => e.status === 'Active' || e.status === 'Active Scheduled').length || 0,
    inactive: equipment?.filter(e => e.status === 'Inactive' || e.status === 'Dormant').length || 0,
    byBranch: equipment?.reduce((acc, e) => {
      const branch = e.branch || 'Unknown';
      return { ...acc, [branch]: (acc[branch] || 0) + 1 };
    }, {} as Record<string, number>) || {},
  }), [equipment]);
}

/**
 * Server-side prefix search for equipment by customer name
 * Uses Firestore range query for efficient filtering
 *
 * @param prefix - The customer name prefix to search (minimum 2 characters)
 * @returns Equipment matching the customer name prefix
 */
export function useDataHubEquipmentByCustomerPrefix(prefix?: string) {
  return useQuery({
    queryKey: dataHubEquipmentKeys.byCustomerPrefix(prefix || ''),
    queryFn: async (): Promise<Equipment[]> => {
      if (!prefix || prefix.length < 2) return [];

      // Firestore prefix search using range query
      // Create end bound by incrementing last character
      const prefixEnd = prefix.slice(0, -1) +
        String.fromCharCode(prefix.charCodeAt(prefix.length - 1) + 1);

      const q = query(
        collection(db, COLLECTIONS.APPSHEET_GENERATORS),
        where('Client Name', '>=', prefix),
        where('Client Name', '<', prefixEnd),
        orderBy('Client Name'),
        limit(50) // Limit results to prevent large fetches
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.filter((d) => !isSoftDeleted(d.data())).map((docSnapshot) => {
        const rawData = docSnapshot.data();
        const { data } = validateDataHubData(
          DataHubGeneratorSchema,
          rawData,
          docSnapshot.id,
          'Generator'
        );
        const generatorData = data as unknown as Omit<DataHubGenerator, 'hubDocId'>;
        return mapGeneratorToEquipment({ ...generatorData, hubDocId: docSnapshot.id });
      });
    },
    enabled: !!prefix && prefix.length >= 2,
    staleTime: 1000 * 60 * 2, // 2 minutes for search results
  });
}

/**
 * Paginated equipment list for browsing large datasets
 * Uses cursor-based pagination with Firestore startAfter
 *
 * @param pageSize - Number of items per page (default: 50)
 * @returns Infinite query with pagination support
 */
export function useDataHubEquipmentPaginated(pageSize = 50) {
  return useInfiniteQuery({
    queryKey: dataHubEquipmentKeys.paginated(),
    queryFn: async ({ pageParam }: { pageParam: DocumentSnapshot | null }) => {
      let q = query(
        collection(db, COLLECTIONS.APPSHEET_GENERATORS),
        orderBy('Client Name'),
        limit(pageSize)
      );

      if (pageParam) {
        q = query(
          collection(db, COLLECTIONS.APPSHEET_GENERATORS),
          orderBy('Client Name'),
          startAfter(pageParam),
          limit(pageSize)
        );
      }

      const snapshot = await getDocs(q);
      // Filter soft-deleted docs from the page items only — the pagination
      // cursor (lastDoc) must still come from the unfiltered snapshot.
      const items = snapshot.docs.filter((d) => !isSoftDeleted(d.data())).map((docSnapshot) => {
        const rawData = docSnapshot.data();
        const { data } = validateDataHubData(
          DataHubGeneratorSchema,
          rawData,
          docSnapshot.id,
          'Generator'
        );
        const generatorData = data as unknown as Omit<DataHubGenerator, 'hubDocId'>;
        return mapGeneratorToEquipment({ ...generatorData, hubDocId: docSnapshot.id });
      });

      return {
        items,
        lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
        hasMore: snapshot.docs.length === pageSize,
      };
    },
    initialPageParam: null as DocumentSnapshot | null,
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.lastDoc : undefined,
    staleTime: 1000 * 60 * 5,
  });
}
